import { useCallback, useRef, useState } from "react";
import { unsentKey } from "./useWorkshopChat";

/** A message written while a private reply was still being written. */
export type QueuedMessage = { id: string; text: string };

/** A queue leaves as one message, each queued message a paragraph of its own, in order. */
export const joinQueued = (texts: string[]) => texts.join("\n\n");

/**
 * The messages a person writes while a private reply is being written. They
 * wait above the composer, in order, and leave together when the reply ends.
 * One queue per thread (a thread not created yet queues under "new"), held in
 * memory only: a reload drops it.
 */
export function useMessageQueue() {
  const [byThread, setByThread] = useState<Record<string, QueuedMessage[]>>({});
  // The source of truth, so a second press before the next render sees the first.
  const ref = useRef(byThread);
  const seq = useRef(0);

  const write = useCallback((key: string, items: QueuedMessage[]) => {
    const next = { ...ref.current };
    if (items.length > 0) next[key] = items;
    else delete next[key];
    ref.current = next;
    setByThread(next);
  }, []);

  const list = useCallback(
    (sessionId: string | null): QueuedMessage[] => byThread[unsentKey(sessionId)] ?? [],
    [byThread],
  );

  const enqueue = useCallback(
    (sessionId: string | null, text: string) => {
      const key = unsentKey(sessionId);
      seq.current += 1;
      write(key, [...(ref.current[key] ?? []), { id: `queued-${seq.current}`, text }]);
    },
    [write],
  );

  /** Take one out; says where it was, so Undo can put it back there. */
  const remove = useCallback(
    (sessionId: string | null, id: string): { item: QueuedMessage; index: number } | null => {
      const key = unsentKey(sessionId);
      const items = ref.current[key] ?? [];
      const index = items.findIndex((m) => m.id === id);
      if (index === -1) return null;
      write(key, items.filter((m) => m.id !== id));
      return { item: items[index], index };
    },
    [write],
  );

  const restore = useCallback(
    (sessionId: string | null, item: QueuedMessage, index: number) => {
      const key = unsentKey(sessionId);
      const items = [...(ref.current[key] ?? [])];
      items.splice(Math.min(index, items.length), 0, item);
      write(key, items);
    },
    [write],
  );

  /** The whole queue, taken out at once to be sent. */
  const take = useCallback(
    (sessionId: string | null): QueuedMessage[] => {
      const key = unsentKey(sessionId);
      const items = ref.current[key] ?? [];
      if (items.length > 0) write(key, []);
      return items;
    },
    [write],
  );

  /** A thread not created yet got its id (its first send or attach): its queue follows. */
  const adopt = useCallback(
    (sessionId: string) => {
      const waiting = ref.current[unsentKey(null)];
      if (!waiting) return;
      write(unsentKey(null), []);
      write(sessionId, [...(ref.current[sessionId] ?? []), ...waiting]);
    },
    [write],
  );

  return { list, enqueue, remove, restore, take, adopt };
}
